/**
 * leadsBoard.js - the pure rules behind the Leads page, shared by the Board
 * (LeadsKanban) and the Table (LeadsTable) so both views always show the same
 * records with the same numbers (Paul, L-11: "the filters apply to both views
 * and carry across when switching").
 *
 * No React and no fetching: views/LeadsView hands in accounts, opportunities
 * and open tasks, and these turn them into cards, filter them, and shape the
 * table rows and the CSV. __tests__/leadsBoard.test.js pins every rule.
 */
import {
  KANBAN_STAGES,
  DEFAULT_STAGE,
  VERTICALS,
  NDA_BADGE,
  stageRank,
  daysInStage,
  normalizeTier,
  verticalOf,
  onLeadsBoard,
  leadStatusOf,
  pickField,
} from '../../data/crmStages';
import { customerKey } from '../../utils/customerMatch';
import { isCancelledStatus } from '../../data/projectStatus';
import { fmtDate } from '../../utils/format';

/** The two board views (L-03). Inactive and Dead never show. */
export const BOARD_STATUSES = ['Active', 'Cold'];

/** Opportunity buckets for the card (Paul, Q5): open = Open; closed = Lost + Cancelled + No Quote.
 *  (There is no Budget status: every Budget bid became Open on 9/29/26. The
 *  opportunity status Dead was renamed Cancelled on 10/1/26, Q-01; an old
 *  Dead row still counts as closed through isCancelledStatus.) */
const OPEN_OPP = ['open'];
const CLOSED_OPP = ['lost', 'cancelled', 'no quote'];

/*
 * Where a card with an OLD stage sits (one the 9/28/26 migration left behind),
 * so nothing vanishes from the board. A display-only echo of that migration's
 * stage map (L-02): anything unknown lands in the first column.
 */
const LEGACY_COLUMN = {
  'Discovery meeting complete': 'Discovery Meeting Complete',
  'NDA sent': 'NDA In-Process',
  'NDA executed': 'NDA Executed',
};

/** The NDA that applies: the record's own, else its company's (company-wide NDA, Paul Q6). */
export function ndaOf(a) {
  const own = pickField(a, 'ndaStatus', 'nda_status');
  if (own) return { status: own, from: null };
  const inh = pickField(a, 'ndaInherited', 'nda_inherited');
  if (inh && inh.status) return { status: inh.status, from: inh.from || null };
  return null;
}

/** L-04 badge: { label, bg, fg, from } or null when there is no NDA on file. */
export function ndaBadge(a) {
  const n = ndaOf(a);
  if (!n) return null;
  const want = String(n.status).trim().toLowerCase();
  const key = Object.keys(NDA_BADGE).find((k) => k.toLowerCase() === want);
  const colors = key ? NDA_BADGE[key] : { bg: '#F5F5F5', fg: '#666' };
  return { status: key || n.status, label: `NDA ${key || n.status}`, ...colors, from: n.from };
}

/** The board column a record sits in. */
export function boardColumn(a) {
  const s = a.stage;
  if (KANBAN_STAGES.includes(s)) return s;
  if (s === 'RFQ received') {
    const n = ndaOf(a);
    return n && String(n.status).toLowerCase() === 'executed' ? 'NDA Executed' : 'Discovery Meeting Complete';
  }
  return LEGACY_COLUMN[s] || DEFAULT_STAGE;
}

const push = (m, k, v) => {
  if (!m.has(k)) m.set(k, []);
  m.get(k).push(v);
};

/**
 * Index opportunities once. A row linked to its site (projects.account_id,
 * Paul Q2) counts on that site only; an older row with no link still counts
 * wherever its Customer name matches - the explicit link wins, so one quote
 * is never counted on two cards.
 */
export function indexOpportunities(projects) {
  const byAccount = new Map();
  const byKey = new Map();
  for (const p of projects || []) {
    const acct = pickField(p, 'accountId', 'account_id');
    if (acct) {
      push(byAccount, acct, p);
      continue;
    }
    const k = customerKey(p.customer);
    if (k) push(byKey, k, p);
  }
  return { byAccount, byKey };
}

/** L-05: { open, closed } for one card. */
export function oppCounts(a, index) {
  const seen = new Map();
  for (const p of index.byAccount.get(a.id) || []) seen.set(p.id ?? p, p);
  const k = customerKey(a.opportunityKey || a.opportunity_key || a.name);
  if (k) for (const p of index.byKey.get(k) || []) if (!seen.has(p.id ?? p)) seen.set(p.id ?? p, p);
  let open = 0;
  let closed = 0;
  for (const p of seen.values()) {
    const s = String(p.status || '')
      .trim()
      .toLowerCase();
    if (OPEN_OPP.includes(s)) open++;
    else if (CLOSED_OPP.includes(s) || isCancelledStatus(s)) closed++;
  }
  return { open, closed };
}

/**
 * Open tasks by record id -> earliest due day ('YYYY-MM-DD', '' if undated).
 * `actions` is the raw /api/crm-actions list; only open account tasks count.
 */
export function indexNextDue(actions) {
  const m = new Map();
  for (const x of actions || []) {
    if (String(x.status || 'open') !== 'open') continue;
    const type = x.entity_type || x.entityType;
    if (type && type !== 'account') continue;
    const id = x.entity_id || x.entityId;
    if (!id) continue;
    const d = String(x.due_date || x.dueDate || '').slice(0, 10);
    const cur = m.get(id);
    if (cur === undefined || (d && (cur === '' || d < cur))) m.set(id, d);
  }
  return m;
}

/**
 * Every lead the page can show (Active and Cold), as cards. `actions` null
 * means the task list has not loaded (or failed): hasTask is then null, so no
 * card is wrongly flagged "No next step".
 */
export function buildLeadCards(accounts, { projects, actions } = {}) {
  const list = accounts || [];
  const nameOf = new Map(list.map((a) => [a.id, a.name]));
  const opps = indexOpportunities(projects);
  const due = actions ? indexNextDue(actions) : null;
  const pool = list.filter((a) => onLeadsBoard(a, false) || onLeadsBoard(a, true));
  // site-less accounts first, then sites - the board's order since Phase 1
  const ordered = [...pool.filter((a) => !a.parentAccountId), ...pool.filter((a) => a.parentAccountId)];
  return ordered.map((a) => {
    const parentName = a.parentAccountId ? pickField(a, 'parentName', 'parent_name') || nameOf.get(a.parentAccountId) || '' : null;
    const nextDue = due ? due.get(a.id) || null : null;
    return {
      id: a.id,
      name: a.name,
      parentName,
      isSite: !!a.parentAccountId,
      status: leadStatusOf(a),
      owner: a.owner || '',
      days: daysInStage(a),
      column: boardColumn(a),
      tier: normalizeTier(pickField(a, 'tierEffective', 'tier_effective') || a.tier),
      vertical: verticalOf(a) || '',
      leadSource: pickField(a, 'leadSource', 'lead_source') || '',
      nda: ndaBadge(a),
      opps: oppCounts(a, opps),
      hasTask: due ? due.has(a.id) : null,
      nextDue,
      review: !!pickField(a, 'stageReview', 'stage_review'),
    };
  });
}

export const DEFAULT_FILTERS = { status: 'Active', owner: 'All', tier: 'All', vertical: 'All', review: false };

/** One card against the filters; `skip` leaves a filter out (used for the chip counts). */
export function matchesFilters(c, f, skip = {}) {
  if (!skip.status && c.status !== f.status) return false;
  if (f.owner !== 'All' && c.owner !== f.owner) return false;
  if (f.tier !== 'All' && c.tier !== f.tier) return false;
  if (f.vertical !== 'All' && c.vertical !== f.vertical) return false;
  if (!skip.review && f.review && !c.review) return false;
  return true;
}

export const filterCards = (cards, f) => cards.filter((c) => matchesFilters(c, f));

/** Counts for the toggle and the review chip, each under the OTHER filters. */
export function filterCounts(cards, f) {
  const out = { Active: 0, Cold: 0, review: 0 };
  for (const c of cards) {
    if (matchesFilters(c, f, { status: true }) && out[c.status] !== undefined) out[c.status]++;
    if (c.review && matchesFilters(c, f, { review: true })) out.review++;
  }
  return out;
}

/** Owner chips: everyone who owns a lead, A-Z. */
export const ownerOptions = (cards) => [
  'All',
  ...[...new Set(cards.map((c) => c.owner).filter(Boolean))].sort((x, y) => x.localeCompare(y)),
];

/** Vertical chips (X-07): only the verticals in use, in the picklist's order; unknown old values last. */
export function verticalOptions(cards) {
  const used = new Set(cards.map((c) => c.vertical).filter(Boolean));
  const known = VERTICALS.filter((v) => used.has(v));
  const other = [...used].filter((v) => !VERTICALS.includes(v)).sort((x, y) => x.localeCompare(y));
  return ['All', ...known, ...other];
}

/* ── Table view (L-11) ───────────────────────────────────────────────────── */

export const TABLE_COLUMNS = [
  { key: 'site', label: 'Site' },
  { key: 'account', label: 'Account' },
  { key: 'stage', label: 'Stage' },
  { key: 'days', label: 'Days in Stage', num: true },
  { key: 'owner', label: 'Owner' },
  { key: 'tier', label: 'Tier' },
  { key: 'vertical', label: 'Vertical' },
  { key: 'leadSource', label: 'Lead Source' },
  { key: 'nda', label: 'NDA Status' },
  { key: 'nextDue', label: 'Next Task Due' },
  { key: 'openOpps', label: 'Open Opportunities', num: true },
];

/** A card as a table row. A site-less account has no Site; its own name is the Account. */
export const tableRow = (c) => ({
  id: c.id,
  site: c.isSite ? c.name : '',
  account: c.isSite ? c.parentName || '' : c.name,
  stage: c.column,
  days: c.days,
  owner: c.owner,
  tier: c.tier,
  vertical: c.vertical,
  leadSource: c.leadSource,
  nda: c.nda ? c.nda.status : '',
  ndaFrom: c.nda ? c.nda.from : null,
  ndaBadge: c.nda,
  nextDue: c.nextDue,
  hasTask: c.hasTask,
  openOpps: c.opps.open,
  review: c.review,
});

const blank = (v) => v == null || v === '';

/** Sort rows by one column. Blanks always sort last; stage sorts in pipeline order. */
export function sortRows(rows, key, dir = 'asc') {
  const sign = dir === 'desc' ? -1 : 1;
  const val = (r) => (key === 'stage' ? stageRank(r.stage) : r[key]);
  return [...rows].sort((x, y) => {
    const a = val(x);
    const b = val(y);
    if (blank(a) && blank(b)) return 0;
    if (blank(a)) return 1;
    if (blank(b)) return -1;
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign;
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }) * sign;
  });
}

/** The CSV of exactly the rows on screen, in their order, with the table's column names. */
export const csvRows = (rows) =>
  rows.map((r) => ({
    Site: r.site,
    Account: r.account,
    Stage: r.stage,
    'Days in Stage': r.days ?? '',
    Owner: r.owner,
    Tier: r.tier,
    Vertical: r.vertical,
    'Lead Source': r.leadSource,
    'NDA Status': r.nda ? (r.ndaFrom ? `${r.nda} (from ${r.ndaFrom})` : r.nda) : '',
    'Next Task Due': r.nextDue ? fmtDate(r.nextDue) : r.hasTask === false ? 'No next step' : '',
    'Open Opportunities': r.openOpps,
  }));
